// Home.tsx - Complete implementation with ShadowButton component
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import MoneyIndicator from './MoneyIndicator';
import ShadowButton from './ShadowButton';
import FeaturedProjectCard from './FeaturedProjectCard';

// Define the project type
interface Project {
  id: string;
  logo: string;
  logoColor: string; 
  title: string;
  description: string;
  year: string;
  status: 'done' | 'archived' | 'planning' | 'active';
  insight?: string;
  moneyDetails?: string;
  moneyTooltip?: string; 
  metric: string;
  readMoreLink?: string;
}

interface Brand {
  name: string;
  logo: string;
}

interface FeaturedProject {
  id: string;
  title: string;
  title_name: string;
  description: string;
  color: string;
  techStack: string;
  readMoreLink?: string;
}

interface Props {
  // You can add any props needed
}

export default function Home({}: Props) {
  // Change default to false to hide details by default
  const [showDetails, setShowDetails] = useState(false);
  
  // State to track which projects are expanded individually
  const [expandedProjects, setExpandedProjects] = useState<Record<string, boolean>>({});
  
  // Function to toggle individual project expansion
  const toggleProjectDetails = (projectId: string) => {
    setExpandedProjects(prev => ({
      ...prev,
      [projectId]: !prev[projectId]
    }));
  };
  
  // Check if a project should show details (either global toggle or individually expanded)
  const shouldShowDetails = (projectId: string) => {
    return showDetails || expandedProjects[projectId];
  };
  
  // Brands data
  const brands: Brand[] = [
    { name: "Lilly", logo: "LY" },
    { name: "Essity", logo: "ES" },
    { name: "Beiersdorf", logo: "BD" },
    { name: "Bundl", logo: "BU" },
    { name: "Bnp-Paribas-Fortis", logo: "BF" },
    { name: "Telenet", logo: "TN" },
    { name: "WGS", logo: "WG" },
    { name: "Galp", logo: "GP" },
    { name: "Aveve", logo: "AV" },
    { name: "Maes", logo: "MA" },
    { name: "Red-Bull", logo: "AV" },
  ];
  
  // Featured projects data
  const featuredProjects: FeaturedProject[] = [
    {
      id: 'kollegin',
      title_name: 'Kollegin',
      title: 'Doctor education platform',
      description: 'Rated 4.8/5 by 200+ doctors, evolving from Webflow MVP to React app',
      color: 'bg-blue-600',
      techStack: 'MVP - GTM - WebFlow - React',
      readMoreLink: '/kollegein'
    },
    {
      id: 'monthlies',
      title_name: 'Monthlies',
      title: 'Women\'s health brand',
      description: 'Validated with 800+ women\'s feedback, launched with first sales',
      color: 'bg-red-600',
      techStack: 'MVP - GTM  - Shopify',
      readMoreLink: '/monthlies'
    },
    {
      id: 'routinely',
      title_name: 'Routinely',
      title: 'Skincare market entry',
      description: 'Built competitor radar analyzing 60+ brands to plan market entry and positioning',
      color: 'bg-gray-600',
      techStack: 'Strategy',
      readMoreLink: '/routinely'
    }
  ];
  
  // Other projects list
  const otherProjects = [
    "Intimelt",
    "LogDrops",
    "KotApp",
    "Dapper",
    "MyGalleryBay",
    "Boostfarm",
    "WP Support"
  ];
  
  // Project data
  const projects: Project[] = [
    {
      id: 'intimelt',
      logo: 'IM',
      logoColor: '#D7A06A',
      title: 'Intimelt',
      description: 'An intimate massage butter to help couples connect.',
      year: '2024 - 2025',
      status: 'active',
      moneyDetails: '',
      moneyTooltip: 'First 7 presales. Figuring out production.',
      insight: '💡 Making a unique product takes time and money.',
      metric: '🚀 First 7 presales.',
    },
    {
      id: 'dobber',
      logo: 'DB',
      logoColor: '#1e90ff',
      title: 'Dobber',
      description: 'Paddleboarding & kayaking without hassle.',
      year: '2025',
      status: 'active',
      moneyDetails: '',
      moneyTooltip: 'Learning React project, no revenue yet.',
      insight: '💡 Friends wanted my gut-tracking spreadsheet, so why not make it an app?',
      metric: '🚀 Go-live in April 2025.'
    },
    {
      id: 'freelance',
      logo: 'FD',
      logoColor: '#3b82f6', // Blue
      title: 'Freelancer',
      description: 'Building WordPress & Webflow sites for SMBs.',
      year: '2010 - 2025',
      status: 'active',
      moneyDetails: '',
      moneyTooltip: 'Steady side-income for 15+ years.',
      insight: '💡 Meaningful business impact with client relationships of 10+ years.',
      metric: ''
      
    },
    {
      id: 'logdrops',
      logo: 'LD',
      logoColor: '#57A9AD',
      title: 'LogDrops',
      description: 'Improve your gut health by logging your bowel movements.',
      year: '2025',
      status: 'active',
      moneyDetails: '',
      moneyTooltip: 'Learning React project, no revenue yet.',
      insight: '💡 Friends wanted my gut-tracking spreadsheet, so why not make it an app?',
      metric: '🚀 Go-live in April 2025.'
    },
    {
      id: 'bundl',
      logo: 'VB',
      logoColor: '#000000',
      title: 'Venture Builder at Bundl',
      description: 'Building Corporate Ventures with global brands.',
      year: '2021 - 2024',  
      status: 'done',
      moneyDetails: '',
      moneyTooltip: 'Full-time salary.',
      insight: '💡 The right people and focus get stuff done.',
      metric: ''
    },
    {
      id: 'bundl-kollegein',
      logo: 'KG',
      logoColor: '#027D87',
      title: 'Kollege:in',
      description: 'Helping doctors share interactive patient cases.',
      year: '2024',
      status: 'done',
      moneyDetails: 'via Bundl',
      moneyTooltip: 'Bundl project.',
      insight: '💡 Design before building and build it right.',
      metric: '',
      readMoreLink: '/kollegein', // Add link
    },
    {
      id: 'bundl-pilly',
      logo: 'PY',
      logoColor: '#987fd1',
      title: 'Pilly',
      description: 'Pitching proven feature impact on treatment adherence.',
      year: '2023',
      status: 'done',
      moneyDetails: 'via Bundl',
      moneyTooltip: 'Bundl project.',
      insight: '💡 People only need a few key features.',
      metric: ''
    },
    {
      id: 'bundl-aipersona',
      logo: 'AI',
      logoColor: '#1e1934',
      title: 'AI Personas',
      description: 'Using Midjourney, Runway and ElevenLabs to create on-brand video personas.',
      year: '2023',
      status: 'done',
      moneyDetails: 'via Bundl',
      moneyTooltip: 'Bundl project.',
      insight: '💡 Creative direction and tool knowledge is essential.',
      metric: ''
    },
    {
      id: 'bundl-monthlies',
      logo: 'ML',
      logoColor: '#E86465',
      title: 'Monthlies',
      description: 'Validating and launching a menstrual care brand to the EU market.',
      year: '2022 - 2023',
      status: 'done',
      moneyDetails: 'via Bundl',
      moneyTooltip: 'Bundl project.',
      insight: '💡 Balance speed vs. quality of tests in multilingual custom Shopify.',
      metric: '',
      readMoreLink: '/monthlies', // Add link
    },
    {
      id: 'bundl-starters',
      logo: 'ST',
      logoColor: '#007348',
      title: 'Starters',
      description: 'How can a bank support different types of starting businesses?',
      year: '2022',
      status: 'done',
      moneyDetails: 'via Bundl',
      moneyTooltip: 'Bundl project.',
      insight: '💡 Creative direction and tool knowledge is essential.',
      metric: ''
    },
    {
      id: 'bundl-stardust',
      logo: 'SD',
      logoColor: '#F8BF20',
      title: 'Stardust',
      description: 'How can we delight support users on top of solving their problem?',
      year: '2021',
      status: 'done',
      moneyDetails: 'via Bundl',
      moneyTooltip: 'Bundl project.',
      insight: '💡 Creative direction and tool knowledge is essential.',
      metric: ''
    },
    {
      id: 'bundl-routinely',
      logo: 'RL',
      logoColor: '#222222',
      title: 'Routinely',
      description: 'D2C skincare market entry strategy and growth opportunity mapping.',
      year: '2021',
      status: 'done',
      moneyDetails: 'via Bundl',
      moneyTooltip: 'Bundl project.',
      insight: '💡 Systematic competitor research creates value down the road.',
      metric: '',
      readMoreLink: '/routinely', // Add link
    }, 
    {
      id: 'aveve',
      logo: 'AV',
      logoColor: '#00936F', 
      title: 'Omnichannel at Aveve',
      description: 'Transforming the Aveve retail customer experience.',
      year: '2018 - 2021',
      status: 'done',
      moneyDetails: '',
      moneyTooltip: 'Full-time salary.',
      insight: '💡 Active and deliberate stakeholder management is crucial and not for rookies like myself.',
      metric: ''
    },
    {
      id: 'kotapp',
      logo: 'KA',
      logoColor: '#8b5cf6', 
      title: 'KotApp',
      description: 'Helping Belgian university students manage their housing & community.',
      year: '2016 - 2017',
      status: 'archived',
      moneyDetails: '',
      moneyTooltip: 'Never launched.',
      insight: '💡 Stop talking and scheming, just build it.',
      metric: ''
    },
    {
      id: 'Dapper',
      logo: 'DP',
      logoColor: '#000000',
      title: 'Dapper',
      description: 'Friends doing design & development for SMBs.',
      year: '2015 - 2017',
      status: 'archived',
      moneyDetails: '',
      moneyTooltip: 'Freelance projects.',
      insight: '💡 Design before developing.',
      metric: ''
    },
    {
      id: 'gallerybay',
      logo: 'GB',
      logoColor: '#F44900',
      title: 'MyGalleryBay',
      description: 'Development for a startup community for artists.',
      year: '2016 - 2017',
      status: 'archived',
      moneyDetails: 'via Dapper',
      moneyTooltip: 'Project based.',
      insight: '💡 Create a clear project plan.',
      metric: ''
    },
    {
      id: 'boostfarm',
      logo: 'BF',
      logoColor: '#F22900', // Amber
      title: 'Boostfarm',
      description: 'SoundCloud play service.',
      year: '2013 - 2014',
      status: 'archived',
      moneyDetails: '',
      moneyTooltip: 'Digital product sales. $Up to 500 months.',
      insight: '💡 Find ways to reach more people and to sustainably scale technology.',
      metric: ''
    },
    {
      id: 'themesupport',
      logo: 'CS',
      logoColor: '#ec4899', // Pink
      title: 'Customer Support',
      description: 'Helping clients set up and modify bestselling WordPress Themes.',
      year: '2008 - 2010',
      status: 'archived',
      moneyDetails: '',
      moneyTooltip: 'Part-time salary.',
      insight: '💡 Trading time for money. Learn to support the difficult stuff.',
      metric: ''
    }
  ];

  // Helper function to capitalize status for display
  const formatStatus = (status: string): string => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };
  
  // Filter out projects to hide
  const hiddenProjectIds = ['gallerybay', 'kotapp'];
  const visibleProjects = projects.filter(
    (project) => !hiddenProjectIds.includes(project.id)
  );

  return (
    <div className="mt-8 md:mt-12">
      {/* Hero Section */}
      <div className="max-w-5xl mx-auto px-4 md:py-5 opacity-0 animate-fade-in-up animation-delay-200 relative">
        {/* Profile image - absolute positioned only on mobile */}

        <div className="flex flex-col md:flex-row md:justify-between md:items-center mt-8 gap-8">
          <div className="md:w-4/5">
            <span className="text-sm px-4 py-2 rounded-full font-medium mb-4 source-code-pro-light" style={{ border: '1px solid #cccccc', display: 'inline-block' }}>
              Venture builder
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">I help build new products and businesses</h1>
            <div className="flex flex-col sm:flex-row gap-3">
            {/* Using ShadowButton component */}
            <ShadowButton href="mailto:christophe@vaneerdt.be" fullWidth>
              Email me
            </ShadowButton>
            <ShadowButton href="tel:+32479706342" fullWidth>
              Call me
            </ShadowButton>
          </div>
          </div>
          
          {/* Profile image - normal positioning on desktop */}
          <div className="md:w-1/3 hidden md:block">
            <img src="/assets/Christophe van Eerdt Product Strategist.png" alt="Christophe van Eerdt - Venture" className="w-full h-auto mx-auto" />
          </div>
        </div>
      </div>
        
      {/* Brands Section - Two Rows Below the Text */}
      <div className="max-w-5xl mx-auto px-4 my-8 sm:my-32 opacity-0 animate-fade-in-up animation-delay-400">
        <div className="flex flex-col items-start md:items-center gap-8">
          {/* Centered sentence */}
          <h2 className="text-base font-medium text-left md:text-center text-gray-700">
            Projects delivered for Fortune 500 companies, global venture builders, and leading brands
          </h2>

          {/* Responsive logo grid with fewer rows */}
          <div className="flex flex-wrap justify-between md:justify-center gap-y-12 md:gap-10 opacity-0 animate-fade-in-up animation-delay-400">
            {brands.map((brand) => (
              <div key={brand.name} className="flex items-center justify-center w-1/3 sm:w-1/4 md:w-auto">
                <img 
                  src={`/assets/logo-${brand.name.toLowerCase()}.svg`} 
                  alt={`${brand.name} logo`} 
                  className="h-8 w-20 sm:h-10 sm:w-24 md:h-12 md:w-32 object-contain opacity-80 dark:invert dark:opacity-70" 
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Featured Ventures */}
      <div className="max-w-5xl mx-auto px-4 my-20 opacity-0 animate-fade-in-up animation-delay-400">
        <h2 className="text-3xl font-bold mb-2">Venture building highlights</h2>
        <p className="text-lg text-gray-700 mb-4">How I approached recent work across the venture development lifecycle</p>
        
        <div className="grid md:grid-cols-3 gap-4">
          {featuredProjects.map((project) => (
            <FeaturedProjectCard key={project.id} project={project} />
          ))}
        </div>
      </div>
        
      {/* Why work with me */}
      <div className="max-w-5xl mx-auto px-4 my-32 opacity-0 animate-fade-in-up animation-delay-500">
        
      <div className="flex flex-col md:flex-row md:items-center gap-10 mb-8">
  <div className="md:w-3/4">
    <h3 className="text-3xl font-bold mb-4">Build with me</h3>
    <p className="text-lg text-gray-700 mb-8">I help teams turn ideas into shipped and profitable products by</p>            
    <ul className="space-y-6 max-w-lg text-lg">
      <li className="flex items-center">
        <div className="mr-4">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-8 h-8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
          </svg>
        </div>
        <div>
          <strong className="font-semibold">Connecting opportunities</strong>
        </div>
      </li>
      <li className="flex items-start">
        <div className="mr-4 mt-1">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-8 h-8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" />
          </svg>
        </div>
        <div>
          <strong className="font-semibold">Making smart decisions</strong>
        </div>
      </li>
      <li className="flex items-start">
        <div className="mr-4 mt-1">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-8 h-8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125-1.125v9.375m-8.25-3 1.5 1.5 3-3.75" />
          </svg>
        </div>
        <div>
          <strong className="font-semibold">Shipping products</strong>
        </div>
      </li>
    </ul>
  </div>
</div>
        {/* Using ShadowButton component */}
        <ShadowButton to="/about">
          More about me
        </ShadowButton>
      </div>
      
      {/* Past Projects Archive */}
      <div className="max-w-5xl mx-auto px-4 mb-16 opacity-0 animate-fade-in-up animation-delay-500 text-left">
        <h2 className="text-2xl md:text-3xl font-bold leading-tight mb-4">Other projects</h2>
        <p className="text-base md:text-lg text-gray-700 mb-4">Things I've worked on for work or play</p>
      
        {/* Unified Projects Card Layout */}
        <div className="w-full">
          {/* Unified Card Layout */}
          <div className="space-y-4 sm:space-y-0">
            {visibleProjects.map((project, index) => (
              <div 
                key={project.id}
                className={`border sm:border-0 sm:border-b border-gray-200 rounded-card p-1 sm:p-0 sm:py-3 sm:px-3 transition-colors duration-200 cursor-default hover:bg-gray-50 ${index % 2 === 1 ? 'bg-[#f9f9f9]' : ''}`}
              >
                <div className="sm:grid sm:grid-cols-12 sm:items-center p-1">
                  {/* Project Title - Takes full width on mobile, 3/12 on desktop */}
                  <div className="flex items-center justify-between sm:col-span-3 mb-2 sm:mb-0">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-6 h-6 rounded flex items-center justify-center text-white font-bold text-xs"
                        style={{ backgroundColor: project.logoColor }}
                      >
                        {project.logo}
                      </div>
                      <span className="font-medium">{project.title}</span>
                    </div>
                    
                    {/* Year - Right aligned on mobile next to title */}
                    <div className="block sm:hidden">
                      <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded border border-gray-200">
                        {project.year}
                      </span>
                    </div>
                  </div>
                  
                  {/* Description - Takes full width on mobile, 7/12 on desktop */}
                  <div className="text-sm text-gray-600 sm:col-span-7 mb-2 sm:mb-0">
                    {project.description}
                  </div>
                  
                  {/* Year - Only visible on desktop, right aligned */}
                  <div className="hidden sm:flex sm:col-span-2 sm:justify-end">
                    <span className="text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded border border-gray-200">
                      {project.year}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      
        <div className="my-24 bg-gray-100 p-12 rounded-lg">
          <h2 className="text-2xl text-center font-bold mb-4">Do you have a similar challenge and want to work with me?</h2>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {/* Using ShadowButton component */}
            <ShadowButton href="mailto:christophe@vaneerdt.be" fullWidth>
              Email me
            </ShadowButton>
            <ShadowButton href="tel:+32479706342" fullWidth>
              Call me
            </ShadowButton>
            <ShadowButton href="https://www.linkedin.com/in/cvaneerdt/" fullWidth>
              LinkedIn
            </ShadowButton>
          </div>
        </div>
      </div>
    </div>
  );
}